import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Plus,
    Library,
    Table2,
    MessageSquare,
    User,
    ChevronDown,
    Check,
    Upload,
    Search,
} from "lucide-react";
import {
    listWorkflows,
    deleteWorkflow,
    updateWorkflow,
    listHiddenWorkflows,
    hideWorkflow,
    unhideWorkflow,
} from "@/app/lib/auditflowApi";
import type { AuditFlowWorkflow } from "../shared/types";
import { BUILT_IN_WORKFLOWS, BUILT_IN_IDS } from "./builtinWorkflows";
import { DisplayWorkflowModal } from "./DisplayWorkflowModal";
import { NewWorkflowModal } from "./NewWorkflowModal";
import { BulkImportWorkflowsModal } from "./BulkImportWorkflowsModal";
import { ToolbarTabs } from "../shared/ToolbarTabs";
import { RowActions } from "../shared/RowActions";
import { AuditFlowIcon } from "@/components/chat/auditflow-icon";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { useColumnResize } from "@/app/hooks/useColumnResize";

type Tab = "all" | "builtin" | "custom" | "hidden";

// Sticky column widths (px): #(32) + checkbox(32) = 64px left offset for name
const NUM_W = "w-8 shrink-0";
const CHECK_W = "w-8 shrink-0";

const COL_DEFAULTS = { name: 300, type: 112, practice: 160, source: 112 };

export function WorkflowList() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { w, startResize } = useColumnResize("workflow_col_widths", COL_DEFAULTS);

    const TABS: { id: Tab; label: string }[] = [
        { id: "all", label: t("workflows", "tabAll") },
        { id: "builtin", label: t("workflows", "tabBuiltin") },
        { id: "custom", label: t("workflows", "tabCustom") },
        { id: "hidden", label: t("workflows", "tabHidden") },
    ];
    const [custom, setCustom] = useState<AuditFlowWorkflow[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<AuditFlowWorkflow | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [activeTab, setActiveTab] = useState<Tab>("all");
    const [newModalOpen, setNewModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [hiddenBuiltinIds, setHiddenBuiltinIds] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [practiceFilter, setPracticeFilter] = useState<string | null>(null);
    const [practiceFilterOpen, setPracticeFilterOpen] = useState(false);
    const [typeFilter, setTypeFilter] = useState<AuditFlowWorkflow["type"] | null>(
        null,
    );
    const [typeFilterOpen, setTypeFilterOpen] = useState(false);
    const [search, setSearch] = useState("");
    const actionsRef = useRef<HTMLDivElement>(null);
    const practiceFilterRef = useRef<HTMLDivElement>(null);
    const typeFilterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        Promise.all([
            listWorkflows("assistant"),
            listWorkflows("tabular"),
            listHiddenWorkflows(),
        ])
            .then(([assistant, tabular, hidden]) => {
                setCustom([...assistant, ...tabular]);
                setHiddenBuiltinIds(hidden);
            })
            .catch(() => setCustom([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setSelectedIds([]);
        setActionsOpen(false);
    }, [activeTab, practiceFilter, typeFilter]);

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (
                actionsRef.current &&
                !actionsRef.current.contains(e.target as Node)
            ) {
                setActionsOpen(false);
            }
        }
        if (actionsOpen) document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [actionsOpen]);

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (
                practiceFilterRef.current &&
                !practiceFilterRef.current.contains(e.target as Node)
            ) {
                setPracticeFilterOpen(false);
            }
            if (
                typeFilterRef.current &&
                !typeFilterRef.current.contains(e.target as Node)
            ) {
                setTypeFilterOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const hiddenBuiltins = BUILT_IN_WORKFLOWS.filter((wf) =>
        hiddenBuiltinIds.includes(wf.id),
    );
    const visibleBuiltins = BUILT_IN_WORKFLOWS.filter(
        (wf) => !hiddenBuiltinIds.includes(wf.id),
    );
    const all = [...visibleBuiltins, ...custom];
    const byTab =
        activeTab === "builtin"
            ? visibleBuiltins
            : activeTab === "custom"
              ? custom
              : activeTab === "hidden"
                ? hiddenBuiltins
                : all;
    const practices = Array.from(
        new Set(byTab.map((wf) => wf.practice).filter((p): p is string => !!p)),
    ).sort();
    const q = search.toLowerCase();
    const filtered = byTab
        .filter((wf) => !practiceFilter || wf.practice === practiceFilter)
        .filter((wf) => !typeFilter || wf.type === typeFilter)
        .filter((wf) => !q || wf.title.toLowerCase().includes(q));

    const allSelected =
        filtered.length > 0 &&
        filtered.every((wf) => selectedIds.includes(wf.id));
    const someSelected =
        !allSelected && filtered.some((wf) => selectedIds.includes(wf.id));

    function toggleAll() {
        if (allSelected) setSelectedIds([]);
        else setSelectedIds(filtered.map((wf) => wf.id));
    }

    function toggleOne(id: string) {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    }

    async function handleHideWorkflow(id: string) {
        setHiddenBuiltinIds((prev) => [...prev, id]);
        await hideWorkflow(id).catch(() => {
            setHiddenBuiltinIds((prev) => prev.filter((x) => x !== id));
        });
    }

    async function handleUnhideWorkflow(id: string) {
        setHiddenBuiltinIds((prev) => prev.filter((x) => x !== id));
        await unhideWorkflow(id).catch(() => {
            setHiddenBuiltinIds((prev) => [...prev, id]);
        });
    }

    async function handleRenameSubmit(workflowId: string) {
        const trimmed = renameValue.trim();
        if (!trimmed) {
            setRenamingId(null);
            return;
        }
        setCustom((prev) =>
            prev.map((w) => (w.id === workflowId ? { ...w, title: trimmed } : w)),
        );
        setRenamingId(null);
        await updateWorkflow(workflowId, { title: trimmed });
    }

    async function handleBulkRemove() {
        const ids = [...selectedIds];
        setActionsOpen(false);
        setSelectedIds([]);
        const builtinIds = ids.filter((id) => BUILT_IN_IDS.has(id));
        const customIds = ids.filter((id) => !BUILT_IN_IDS.has(id));
        if (builtinIds.length > 0) {
            setHiddenBuiltinIds((prev) => [
                ...prev,
                ...builtinIds.filter((id) => !prev.includes(id)),
            ]);
            await Promise.all(
                builtinIds.map((id) => hideWorkflow(id).catch(() => {})),
            );
        }
        if (customIds.length > 0) {
            await Promise.all(
                customIds.map((id) => deleteWorkflow(id).catch(() => {})),
            );
            setCustom((prev) => prev.filter((w) => !customIds.includes(w.id)));
        }
    }

    async function handleBulkUnhide() {
        const ids = [...selectedIds];
        setActionsOpen(false);
        setSelectedIds([]);
        setHiddenBuiltinIds((prev) => prev.filter((id) => !ids.includes(id)));
        await Promise.all(ids.map((id) => unhideWorkflow(id).catch(() => {})));
    }

    const getTypeMeta = (type: AuditFlowWorkflow["type"]) =>
        type === "tabular"
            ? { label: t("workflows", "typeTabular"), Icon: Table2, chipBg: "bg-violet-50", chipColor: "text-violet-500" }
            : {
                  label: t("workflows", "typeAssistant"),
                  Icon: MessageSquare,
                  chipBg: "bg-blue-50",
                  chipColor: "text-blue-500",
              };

    const typeFilterButton = (
        <div className="relative" ref={typeFilterRef}>
            <button
                onClick={() => setTypeFilterOpen((o) => !o)}
                className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                    typeFilter
                        ? "text-gray-700 hover:text-gray-900"
                        : "text-gray-500 hover:text-gray-700"
                }`}
            >
                {typeFilter
                    ? typeFilter === "tabular"
                        ? t("workflows", "typeTabular")
                        : t("workflows", "typeAssistant")
                    : t("workflows", "filterByType")}
                <ChevronDown className="h-3 w-3" />
            </button>
            {typeFilterOpen && (
                <div className="absolute right-0 top-full mt-1.5 z-20 w-40 rounded-xl border border-gray-100 bg-white shadow-lg overflow-hidden">
                    <button
                        onClick={() => {
                            setTypeFilter(null);
                            setTypeFilterOpen(false);
                        }}
                        className="flex items-center justify-between w-full px-3 py-2 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                        {t("workflows", "allTypes")}
                        {!typeFilter && (
                            <Check className="h-3.5 w-3.5 text-gray-400" />
                        )}
                    </button>
                    <div className="border-t border-gray-100" />
                    {(["assistant", "tabular"] as const).map((tp) => {
                        const { label, Icon, chipColor } = getTypeMeta(tp);
                        return (
                            <button
                                key={tp}
                                onClick={() => {
                                    setTypeFilter(tp);
                                    setTypeFilterOpen(false);
                                }}
                                className="flex items-center justify-between w-full px-3 py-2 text-xs hover:bg-gray-50 transition-colors"
                            >
                                <span
                                    className={`inline-flex items-center gap-1.5 font-medium ${chipColor}`}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {label}
                                </span>
                                {typeFilter === tp && (
                                    <Check className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const practiceFilterButton = (
        <div className="relative" ref={practiceFilterRef}>
            <button
                onClick={() => setPracticeFilterOpen((o) => !o)}
                className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                    practiceFilter
                        ? "text-gray-700 hover:text-gray-900"
                        : "text-gray-500 hover:text-gray-700"
                }`}
            >
                {practiceFilter ?? t("workflows", "filterByPractice")}
                <ChevronDown className="h-3 w-3" />
            </button>
            {practiceFilterOpen && (
                <div className="absolute right-0 top-full mt-1.5 z-20 w-52 rounded-xl border border-gray-100 bg-white shadow-lg overflow-hidden">
                    <button
                        onClick={() => {
                            setPracticeFilter(null);
                            setPracticeFilterOpen(false);
                        }}
                        className="flex items-center justify-between w-full px-3 py-2 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                        {t("workflows", "allPractices")}
                        {!practiceFilter && (
                            <Check className="h-3.5 w-3.5 text-gray-400" />
                        )}
                    </button>
                    {practices.length > 0 && (
                        <div className="border-t border-gray-100" />
                    )}
                    {practices.map((p) => (
                        <button
                            key={p}
                            onClick={() => {
                                setPracticeFilter(p);
                                setPracticeFilterOpen(false);
                            }}
                            className="flex items-center justify-between w-full px-3 py-2 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            <span className="truncate pr-2">{p}</span>
                            {practiceFilter === p && (
                                <Check className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const toolbarActions = (
        <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
                <div ref={actionsRef} className="relative">
                    <button
                        onClick={() => setActionsOpen((v) => !v)}
                        className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors"
                    >
                        {t("common", "actions")}
                        <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    {actionsOpen && (
                        <div className="absolute top-full right-0 mt-1 w-36 rounded-lg border border-gray-100 bg-white shadow-lg z-50 overflow-hidden">
                            {activeTab === "hidden" ? (
                                <button
                                    onClick={handleBulkUnhide}
                                    className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    {t("workflows", "unhide")}
                                </button>
                            ) : (
                                <button
                                    onClick={handleBulkRemove}
                                    className="w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50 transition-colors"
                                >
                                    {t("common", "delete")}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
            {typeFilterButton}
            {practiceFilterButton}
        </div>
    );

    return (
        <div className="flex flex-col flex-1 overflow-hidden bg-white">
            {/* Page header — title only */}
            <div className="flex items-center px-8 py-4 shrink-0">
                <h1 className="text-2xl font-medium font-serif text-gray-900">
                    {t("workflows", "title")}
                </h1>
            </div>

            {/* Tab filters */}
            <ToolbarTabs
                tabs={TABS}
                active={activeTab}
                onChange={setActiveTab}
                actions={toolbarActions}
            />

            {/* Action bar — search + CTA buttons, Legora-style */}
            <div className="flex items-center gap-2 px-8 py-2.5 border-b border-gray-100 shrink-0">
                {/* Search */}
                <div className="flex items-center gap-2 h-8 px-3 bg-white border border-gray-200 rounded-lg flex-1 max-w-xs">
                    <Search className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <input
                        type="text"
                        placeholder={t("workflows", "searchPlaceholder")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-1 text-xs text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                    />
                </div>

                <div className="flex items-center gap-2 ml-auto">
                    {/* Import button */}
                    <button
                        onClick={() => setImportModalOpen(true)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <Upload className="h-3.5 w-3.5" />
                        {t("workflows", "importWorkflows")}
                    </button>

                    {/* New Workflow button */}
                    <button
                        onClick={() => setNewModalOpen(true)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        {t("workflows", "newWorkflow")}
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
                <div className="min-w-max">
                    {/* Column headers */}
                    <div className="flex items-center h-8 pr-8 border-b border-gray-200 text-xs text-gray-500 font-medium select-none">
                        {/* # number column */}
                        <div className={`sticky left-0 z-[60] ${NUM_W} relative bg-white flex items-center justify-center self-stretch text-gray-400`}>
                            #
                        </div>
                        {/* Checkbox column */}
                        <div className={`sticky left-8 z-[60] ${CHECK_W} relative bg-white flex items-center justify-center self-stretch before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-white`}>
                            {!loading && (
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = someSelected;
                                    }}
                                    onChange={toggleAll}
                                    className="h-2.5 w-2.5 rounded border-gray-200 cursor-pointer accent-black"
                                />
                            )}
                        </div>
                        {/* Name column — sticky after # + checkbox = left-16 */}
                        <div
                            className="sticky left-16 z-[60] shrink-0 relative bg-white pl-2 self-stretch flex items-center border-r border-gray-200"
                            style={{ width: w("name") }}
                        >
                            {t("workflows", "colName")}
                            <div
                                className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10"
                                onMouseDown={(e) => startResize("name", e)}
                            />
                        </div>
                        <div
                            className="ml-auto shrink-0 relative pl-2 self-stretch flex items-center border-r border-gray-200"
                            style={{ width: w("type") }}
                        >
                            {t("workflows", "colType")}
                            <div
                                className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10"
                                onMouseDown={(e) => startResize("type", e)}
                            />
                        </div>
                        <div
                            className="shrink-0 relative pl-2 self-stretch flex items-center border-r border-gray-200"
                            style={{ width: w("practice") }}
                        >
                            {t("workflows", "colPractice")}
                            <div
                                className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10"
                                onMouseDown={(e) => startResize("practice", e)}
                            />
                        </div>
                        <div
                            className="shrink-0 relative pl-2 self-stretch flex items-center"
                            style={{ width: w("source") }}
                        >
                            {t("workflows", "colSource")}
                            <div
                                className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 transition-colors z-10"
                                onMouseDown={(e) => startResize("source", e)}
                            />
                        </div>
                        <div className="w-8 shrink-0" />
                    </div>

                    {loading && activeTab !== "builtin" ? (
                        <div>
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="flex items-center h-10 pr-8 border-b border-gray-50"
                                >
                                    <div className="w-8 shrink-0" />
                                    <div className="w-8 shrink-0" />
                                    <div className="flex-1 min-w-0 pl-3 pr-4">
                                        <div className="h-3.5 w-48 rounded bg-gray-100 animate-pulse" />
                                    </div>
                                    <div className="w-28 shrink-0">
                                        <div className="h-3 w-16 rounded bg-gray-100 animate-pulse" />
                                    </div>
                                    <div className="w-40 shrink-0">
                                        <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
                                    </div>
                                    <div className="w-28 shrink-0">
                                        <div className="h-3 w-14 rounded bg-gray-100 animate-pulse" />
                                    </div>
                                    <div className="w-8 shrink-0" />
                                </div>
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-start py-24 w-full max-w-xs mx-auto">
                            {activeTab === "custom" ? (
                                <>
                                    <Library className="h-8 w-8 text-gray-300 mb-4" />
                                    <p className="text-2xl font-medium font-serif text-gray-900">
                                        {t("workflows", "customWorkflowsTitle")}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-400 text-left">
                                        {t("workflows", "customWorkflowsDesc")}
                                    </p>
                                    <button
                                        onClick={() => setNewModalOpen(true)}
                                        className="mt-4 inline-flex items-center gap-1 rounded-full bg-gray-900 px-3 py-1 text-xs font-medium text-white hover:bg-gray-700 transition-colors shadow-md"
                                    >
                                        {t("projects", "createNew")}
                                    </button>
                                </>
                            ) : activeTab === "hidden" ? (
                                <>
                                    <Library className="h-8 w-8 text-gray-300 mb-4" />
                                    <p className="text-2xl font-medium font-serif text-gray-900">
                                        {t("workflows", "hiddenWorkflowsTitle")}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-400 text-left">
                                        {t("workflows", "hiddenWorkflowsDesc")}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <Library className="h-8 w-8 text-gray-300 mb-4" />
                                    <p className="text-2xl font-medium font-serif text-gray-900">
                                        {t("workflows", "title")}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-400 text-left">
                                        {t("workflows", "emptyDesc")}
                                    </p>
                                </>
                            )}
                        </div>
                    ) : (
                        filtered.map((wf, index) => {
                            const rowBg = selectedIds.includes(wf.id)
                                ? "bg-gray-50"
                                : "bg-white";
                            const { Icon: TypeIcon, chipBg, chipColor } = getTypeMeta(wf.type);
                            return (
                            <div
                                key={wf.id}
                                onClick={() => {
                                    if (renamingId === wf.id) return;
                                    setSelected(wf);
                                }}
                                className="group flex items-center h-10 pr-8 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                                {/* Row number */}
                                <div
                                    className={`sticky left-0 z-[60] ${NUM_W} flex items-center justify-center self-stretch text-xs text-gray-400 ${rowBg} group-hover:bg-gray-50`}
                                >
                                    {index + 1}
                                </div>
                                {/* Checkbox */}
                                <div
                                    className={`sticky left-8 z-[60] ${CHECK_W} p-2 flex items-center justify-center ${rowBg} group-hover:bg-gray-50`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(wf.id)}
                                        onChange={() => toggleOne(wf.id)}
                                        className="h-2.5 w-2.5 rounded border-gray-200 cursor-pointer accent-black"
                                    />
                                </div>
                                {/* Name cell — sticky at left-16 */}
                                <div
                                    className={`sticky left-16 z-[60] shrink-0 px-2 flex items-center gap-2 ${rowBg} group-hover:bg-gray-50 border-r border-gray-200 self-stretch`}
                                    style={{ width: w("name") }}
                                >
                                    {/* Type icon chip — inspired by Legora PDF chip style */}
                                    <span
                                        className={`inline-flex items-center justify-center h-6 w-6 rounded-md shrink-0 ${chipBg}`}
                                    >
                                        <TypeIcon className={`h-3.5 w-3.5 ${chipColor}`} />
                                    </span>
                                    {renamingId === wf.id ? (
                                        <input
                                            autoFocus
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleRenameSubmit(wf.id);
                                                if (e.key === "Escape") setRenamingId(null);
                                            }}
                                            onBlur={() => handleRenameSubmit(wf.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="flex-1 min-w-0 text-sm text-gray-800 bg-transparent outline-none"
                                        />
                                    ) : (
                                        <span className="text-sm text-gray-800 truncate">
                                            {wf.title}
                                        </span>
                                    )}
                                </div>
                                <div className="ml-auto shrink-0 pl-2 border-r border-gray-200 self-stretch flex items-center" style={{ width: w("type") }}>
                                    {(() => {
                                        const { label, Icon, chipColor } = getTypeMeta(wf.type);
                                        return (
                                            <span
                                                className={`inline-flex items-center gap-1.5 text-xs font-medium ${chipColor}`}
                                            >
                                                <Icon className="h-3.5 w-3.5" />
                                                {label}
                                            </span>
                                        );
                                    })()}
                                </div>
                                <div className="shrink-0 pl-2 truncate border-r border-gray-200 self-stretch flex items-center" style={{ width: w("practice") }}>
                                    {wf.practice ? (
                                        <span className="text-xs font-medium text-gray-600">
                                            {wf.practice}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-300">
                                            —
                                        </span>
                                    )}
                                </div>
                                <div className="shrink-0 pl-2 overflow-hidden" style={{ width: w("source") }}>
                                    {wf.is_system ? (
                                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                            <AuditFlowIcon size={14} />
                                            AuditFlow
                                        </span>
                                    ) : wf.user_id === user?.id ? (
                                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                            <User className="h-3.5 w-3.5 text-gray-500" />
                                            {t("workflows", "myCreation")}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 truncate max-w-full">
                                            <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                            <span className="truncate">
                                                {wf.shared_by_name ?? t("workflows", "sharedFallback")}
                                            </span>
                                        </span>
                                    )}
                                </div>
                                <div
                                    className="w-8 shrink-0 flex justify-end"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {wf.is_system ? (
                                        activeTab === "hidden" ? (
                                            <RowActions
                                                onUnhide={() =>
                                                    handleUnhideWorkflow(wf.id)
                                                }
                                            />
                                        ) : (
                                            <RowActions
                                                onHide={() =>
                                                    handleHideWorkflow(wf.id)
                                                }
                                            />
                                        )
                                    ) : wf.is_owner === false ? null : (
                                        <RowActions
                                            onRename={() => {
                                                setRenameValue(wf.title);
                                                setRenamingId(wf.id);
                                            }}
                                            onDelete={async () => {
                                                await deleteWorkflow(wf.id);
                                                setCustom((prev) =>
                                                    prev.filter(
                                                        (w) => w.id !== wf.id,
                                                    ),
                                                );
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                            );
                        })
                    )}
                </div>
            </div>

            <DisplayWorkflowModal
                workflows={all}
                workflow={selected}
                onClose={() => setSelected(null)}
            />

            <NewWorkflowModal
                open={newModalOpen}
                onClose={() => setNewModalOpen(false)}
                onCreated={(wf) => {
                    setCustom((prev) => [wf, ...prev]);
                    setNewModalOpen(false);
                    navigate(`/workflows/${wf.id}`);
                }}
            />

            <BulkImportWorkflowsModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                onImported={(wfs) => {
                    setCustom((prev) => [...wfs, ...prev]);
                    setImportModalOpen(false);
                }}
            />
        </div>
    );
}
